import { warn } from "../shared/debug";

/**
 * A typed, synchronous event channel — the one way the scanner and the view talk.
 *
 * Two channels make a link, one in each direction, and each side is handed an
 * {@link Endpoint} that can only *emit* its own events and *listen* for the other side's.
 * Direction is therefore enforced by the types: the scanner cannot subscribe to its own
 * `frame` event by mistake, and the view cannot emit one.
 *
 * Synchronous on purpose. A `framing` change from a gesture has to reach the scan loop
 * before its next iteration, and a `session:started` has to be answered with a framing
 * before the loop's first frame — a queued delivery would turn both into races. What
 * synchrony must not do is let a listener's failure travel back into the emitter: a
 * throw in a view handler would land in the scan loop's `catch`, count as a failed
 * iteration and, eight of them later, stop the scanner over a rendering bug. So every
 * listener runs isolated, and a failure is logged rather than propagated.
 */

export type Listener<T> = (payload: T) => void;

/**
 * Emit arguments: nothing for an event declared `void`, the payload for anything else.
 * Lets `emit("start")` read as the command it is, while `emit("frame", …)` cannot be
 * called without its frame.
 */
type EmitArgs<E, K extends keyof E> = E[K] extends void ? [] : [payload: E[K]];

export interface Emitter<E> {
  emit<K extends keyof E>(type: K, ...args: EmitArgs<E, K>): void;
}

export interface Subscriber<E> {
  /** Subscribe; returns the unsubscribe. */
  on<K extends keyof E>(type: K, listener: Listener<E[K]>): () => void;
}

/** One side of a link: emits `Out`, listens for `In`. */
export interface Endpoint<Out, In> extends Emitter<Out>, Subscriber<In> {}

export class Channel<E extends object> implements Emitter<E>, Subscriber<E> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly listeners = new Map<keyof E, Set<Listener<any>>>();

  on<K extends keyof E>(type: K, listener: Listener<E[K]>): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(listener);
    return () => void set.delete(listener);
  }

  emit<K extends keyof E>(type: K, ...args: EmitArgs<E, K>): void {
    const set = this.listeners.get(type);
    if (!set) return;
    const payload = args[0] as E[K];
    // Over a copy: a listener that unsubscribes itself, or subscribes another, must not
    // change who receives this emission.
    for (const listener of [...set]) {
      try {
        listener(payload);
      } catch (error) {
        warn("bus", `listener for "${String(type)}" failed`, error);
      }
    }
  }
}

/**
 * Two channels, wired as a pair of endpoints.
 *
 * `a` emits `AtoB` and hears `BtoA`; `b` the reverse. Nothing about the transport is
 * specific to the scanner and the view — the protocol module names the two sides.
 */
export function link<AtoB extends object, BtoA extends object>(): {
  a: Endpoint<AtoB, BtoA>;
  b: Endpoint<BtoA, AtoB>;
} {
  const forward = new Channel<AtoB>();
  const backward = new Channel<BtoA>();
  return {
    a: {
      emit: (type, ...args) => forward.emit(type, ...args),
      on: (type, listener) => backward.on(type, listener),
    },
    b: {
      emit: (type, ...args) => backward.emit(type, ...args),
      on: (type, listener) => forward.on(type, listener),
    },
  };
}
